<script>
import store from '@state/store'
import { parseFromTimeZone, formatToTimeZone } from 'date-fns-timezone'
import CommentItem from '@router/components/comment-item.vue'
import UserProfile from '@companies/components/user-profile.vue'
import PostEdit from '@router/components/post-edit.vue'
import { authComputed, authMethods } from '@state/helpers'
import { format, parseISO } from 'date-fns/fp'

export default {
  name: 'PostItem',
  components: {
    CommentItem,
    UserProfile,
    PostEdit,
  },
  props: {
    onePost: { type: Object, required: true },
    isLast: { type: Boolean, required: true },
    showCommentSection: { type: Boolean, required: false },
  },
  data() {
    return {
      userProfile: false,
      showComment: false,
      newComment: '',
      postTypes: {
        gratitude: { title: 'Gratitude', color: 'gratitude_purple' },
        kyndness: { title: 'Act of Kindness', color: 'aok_green' },
        volunteered: { title: 'Volunteered', color: 'volunteer_red' },
        donation: { title: 'Donation', color: 'donation' },
        note: { title: '', color: 'donation' },
        event: { title: 'Created An Event', color: 'blue-light' },
        event_joined: { title: 'Joined An Event', color: 'blue-light' },
      },
      showPostEdit: false,
    }
  },
  computed: {
    ...authComputed,
    postDate() {
      return formatToTimeZone(
        parseFromTimeZone(this.onePost.created_at, {
          timeZone: 'GMT',
        }),
        'MMMM D, YYYY h:mma',
        {
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }
      )
    },
    postType() {
      return this.postTypes[this.onePost.type].title.toUpperCase()
    },
    postColor() {
      return this.postTypes[this.onePost.type].color
    },
    organization() {
      return this.onePost.organization != null
        ? this.onePost.organization.name.toUpperCase()
        : ''
    },
    needToShowMedia() {
      let counter = 0
      if (this.onePost.image_url) {
        if (this.onePost.image_url.length > 0) {
          counter += this.onePost.image_url.length
        }
      }
      if (this.onePost.video_url) {
        if (this.onePost.video_url.length > 0) {
          counter += this.onePost.video_url.length
        }
      }
      return counter
    },
    imageToShow() {
      return this.needToShowMedia === 1 ? 1 : 0
    },
  },
  mounted() {
    this.showComment = this.showCommentSection
  },
  methods: {
    ...authMethods,
    addLike() {
      if (this.onePost.is_likeable) {
        this.$emit('add-like', this.onePost.id)
      }
    },
    likeComment(comment) {
      if (comment.is_likeable) {
        this.$emit('like-comment', {
          statusUpdateId: this.onePost.id,
          commentId: comment.id,
        })
      }
    },
    postComment() {
      this.$emit('post-comment', {
        statusUpdateId: this.onePost.id,
        comment: this.newComment,
      })
      this.newComment = ''
    },
    openUserProfile() {
      this.userProfile = this.onePost.user
    },
    getIframeSrc(videoId) {
      return 'https://player.vimeo.com/video/' + videoId
    },
    changeStatus(action) {
      let newStatus = 'hidden'
      if (action === 'hide' && this.onePost.status === 'hidden') {
        newStatus = 'approved'
      }
      if (action === 'delete') {
        newStatus = 'deleted'
      }
      this.$emit('change-status', {
        statusUpdateId: this.onePost.id,
        status: newStatus,
      })
    },
    closePostEdit() {
      this.showPostEdit = false
    },
    saveStatusUpdate(params) {
      store
        .dispatch('companies/saveStatusUpdate', { post: params })
        .then((newPost) => {
          if (newPost) this.closePostEdit()
          // reset and hide form
          // this.refreshPostForm = newPost.id
          // show kynd points
          // this.lastPostPoints = newPost.points
          // this.showEarnedPoints = true
        })
        .catch(() => {})
    },
    closeUserView() {
      this.userProfile = false
    },
    openEventModal(post) {
      this.$router.push({
        name: 'eventDetails',
        params: {
          companyId: post.opportunity.group.company_id,
          eventId: post.opportunity.id,
        },
      })
    },
    startDateFormatted(start) {
      return format('EEE, MMM d, Y', parseISO(start))
    },
    hoursFormatted(start, end) {
      return (
        format('hh:mm a', parseISO(start)) +
        ' - ' +
        format('hh:mm a', parseISO(end))
      )
    },
  },
}
</script>

<template>
  <div
    v-if="
      (currentUser.id === onePost.user_id &&
        (onePost.status === 'hidden' || onePost.status === 'approved')) ||
        onePost.status === 'approved' ||
        canEditCompany(onePost.updatable_id)
    "
    class="post-container linked-posts pt-5"
    :class="[
      onePost.status === 'hidden' ? 'bg-gray' : '',
      onePost.status === 'deleted' ? 'bg-volunteer_red_transparent' : '',
    ]"
  >
    <div :class="`border-${postColor}`">
      <div class="pl-3.5">
        <img
          :src="onePost.user.image_url"
          class="post-avatar cursor-pointer"
          @click="openUserProfile"
        />
        <div class="post-details">
          <div class="">
            <span
              v-if="
                onePost.kyndness_type === 'receive' && onePost.receiver_id > 0
              "
              class="post-author-name"
            >
              <a class="cursor-pointer" @click="openUserProfile">{{
                onePost.user.name
              }}</a>
              did an act of kyndness for
              <a class="cursor-pointer" @click="openUserProfile">{{
                onePost.giver.name
              }}</a
              ><br
            /></span>
            <span
              v-if="
                onePost.kyndness_type === 'receive' && onePost.receiver_id === 0
              "
              class="post-author-name"
            >
              <a class="cursor-pointer" @click="openUserProfile">{{
                onePost.user.name
              }}</a>
              received an act of kyndness<br
            /></span>
            <span
              v-if="
                onePost.kyndness_type === 'witnessed' && onePost.receiver_id > 0
              "
              class="post-author-name"
            >
              <a class="cursor-pointer" @click="openUserProfile">{{
                onePost.user.name
              }}</a>
              did an act of kyndness witnessed by
              <a class="cursor-pointer" @click="openUserProfile">{{
                onePost.giver.name
              }}</a
              ><br
            /></span>
            <span
              v-if="
                onePost.kyndness_type === 'witnessed' &&
                  onePost.receiver_id === 0
              "
              class="post-author-name"
            >
              <a class="cursor-pointer" @click="openUserProfile">{{
                onePost.user.name
              }}</a>
              witnessed an act of kyndness<br
            /></span>
            <span
              v-if="
                onePost.kyndness_type === 'give' ||
                  onePost.kyndness_type === '' ||
                  onePost.kyndness_type === '0'
              "
              class="post-author-name"
            >
              <a class="cursor-pointer" @click="openUserProfile">{{
                onePost.user.name
              }}</a></span
            >
            <span class="post-date">{{ postDate }}</span>
          </div>
          <div class="post-type" :class="`text-${postColor}`">
            {{ postType }}
            <span v-if="onePost.type === 'volunteered'"
              >{{ parseFloat(onePost.hours).toString() }} HOURS</span
            ><span v-if="onePost.organization">
              |
              <router-link
                :to="{
                  name: 'organization',
                  params: { organizationId: onePost.organization.id },
                }"
                >{{ organization }}</router-link
              ></span
            >
          </div>
        </div>
      </div>
      <div class="pr-9">
        <v-popover
          v-if="
            onePost.user_id === currentUser.id ||
              onePost.receiver_id === currentUser.id ||
              canEditCompany(onePost.updatable_id)
          "
          trigger="click"
          placement="bottom"
          :auto-hide="true"
          :hide-on-target-click="true"
        >
          <svg
            class="tooltip-target cursor-pointer"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1000 1000.23"
            fill="#069BD7"
            height="22"
            width="22"
          >
            <title>Kyndhub edit icon</title>
            <path
              d="M499.86,315.82c101.73,0,184.22,82.43,184.25,184.16v.09c0,101.73-82.43,184.22-184.16,184.25h-.09a184.25,184.25,0,0,1,0-368.5Zm0,52.65A131.58,131.58,0,1,0,631.55,500h0A131.6,131.6,0,0,0,500,368.42h-.09ZM805.4,693.93a26.29,26.29,0,0,1-4.62-31.2,338.44,338.44,0,0,0,26.76-65A26.32,26.32,0,0,1,852.73,579h94.44V421.12h-94.3a26.29,26.29,0,0,1-25.19-18.77,338.7,338.7,0,0,0-26.76-65,26.29,26.29,0,0,1,4.62-31.2l66.65-66.7L760.56,127.83l-66.79,66.79a26.31,26.31,0,0,1-31.16,4.63,341.46,341.46,0,0,0-65-26.81,26.35,26.35,0,0,1-18.76-25.24V52.81H420.91V147.2a26.35,26.35,0,0,1-18.77,25.24,342,342,0,0,0-65,26.81A26.3,26.3,0,0,1,306,194.62L239.35,128,127.67,239.46l66.65,66.65A26.33,26.33,0,0,1,199,337.32a337.57,337.57,0,0,0-26.77,65A26.3,26.3,0,0,1,147,421.07H52.46V579H146.9a26.32,26.32,0,0,1,25.19,18.77,338.44,338.44,0,0,0,26.76,65,26.29,26.29,0,0,1-4.62,31.2l-66.65,66.66L239.21,872.22l66.65-66.66A26.31,26.31,0,0,1,337,800.94,338,338,0,0,0,402,827.7a26.35,26.35,0,0,1,18.86,25.24v94.43h158V852.94a26.35,26.35,0,0,1,18.86-25.24,338,338,0,0,0,64.94-26.76,26.31,26.31,0,0,1,31.16,4.62l66.65,66.66L872.19,760.58Zm50.84-23.71L928,742a26.3,26.3,0,0,1,0,37.2h0L779.14,928.05a26.34,26.34,0,0,1-37.21,0l-71.78-71.78a392.43,392.43,0,0,1-38.6,15.85V973.81a26.35,26.35,0,0,1-26.3,26.31H394.61a26.35,26.35,0,0,1-26.3-26.31V872.12a392.43,392.43,0,0,1-38.6-15.85l-71.78,71.78a26.34,26.34,0,0,1-37.21,0L71.88,779.21a26.3,26.3,0,0,1,0-37.19l0,0,71.74-71.78a392.5,392.5,0,0,1-15.86-38.6H26.3A26.3,26.3,0,0,1,0,605.32H0V394.77a26.36,26.36,0,0,1,26.3-26.3H128a392.5,392.5,0,0,1,15.86-38.6L72.11,258.09a26.31,26.31,0,0,1,0-37.2h0L221,72a26.36,26.36,0,0,1,37.21,0l71.55,71.79a395.25,395.25,0,0,1,38.6-15.9V26.19A26.35,26.35,0,0,1,394.61-.12H605.16a26.36,26.36,0,0,1,26.3,26.31V127.88a395.25,395.25,0,0,1,38.6,15.9L741.84,72a26.36,26.36,0,0,1,37.21,0L927.89,220.88a26.3,26.3,0,0,1,0,37.19l0,0-71.74,71.78A392.5,392.5,0,0,1,872,368.47H973.7a26.36,26.36,0,0,1,26.3,26.3V605.32a26.3,26.3,0,0,1-26.3,26.3H872.19a390.36,390.36,0,0,1-16,38.6Z"
              transform="translate(0 0.12)"
            />
          </svg>
          <template slot="popover">
            <div class="text-right text-blue-darker text-lg">
              <a
                v-if="
                  onePost.status !== 'deleted' &&
                    (onePost.user_id === currentUser.id ||
                      onePost.receiver_id === currentUser.id ||
                      canEditCompany(onePost.updatable_id))
                "
                v-close-popover
                class="block cursor-pointer"
                @click="changeStatus('hide')"
                >{{ onePost.status === 'approved' ? 'Hide' : 'Unhide' }}</a
              >
              <a
                v-if="
                  (onePost.user_id === currentUser.id ||
                    onePost.receiver_id === currentUser.id ||
                    canEditCompany(onePost.updatable_id)) &&
                    onePost.type !== 'event' &&
                    onePost.type !== 'event_joined'
                "
                v-close-popover
                class="block cursor-pointer"
                @click="showPostEdit = true"
                >Edit</a
              >
              <a
                v-if="
                  onePost.status !== 'deleted' &&
                    (onePost.user_id === currentUser.id ||
                      onePost.receiver_id === currentUser.id ||
                      canEditCompany(onePost.updatable_id))
                "
                v-close-popover
                class="block cursor-pointer"
                @click="changeStatus('delete')"
                >Delete</a
              >
            </div>
          </template>
        </v-popover>
      </div>
    </div>
    <div
      v-if="onePost.type !== 'event' && onePost.type !== 'event_joined'"
      class="post-content"
      v-html="onePost.text_with_links"
    >
    </div>
    <div
      v-if="
        (onePost.type === 'event' || onePost.type === 'event_joined') &&
          onePost.opportunity_id !== 0
      "
      class="cursor-pointer event-box ml-5 mt-5 mr-5 mb-0 w-8/12"
      @click="openEventModal(onePost)"
    >
      <div class="event-image">
        <img
          :src="onePost.opportunity.image_url"
          loading="lazy"
          :alt="onePost.opportunity.name"
        />
      </div>
      <div class="m-4.5">
        <h3 class="event-title">{{ onePost.opportunity.name }}</h3>
      </div>
      <div class="event-meta">
        <div class="">
          <span class="event-date">{{
            startDateFormatted(onePost.opportunity.start)
          }}</span>
          <span class="event-time">{{
            hoursFormatted(onePost.opportunity.start, onePost.opportunity.end)
          }}</span>
        </div>
        <div class="text-volunteer_red">
          {{ onePost.opportunity.joined_users_count }}/{{
            onePost.opportunity.max_people
          }}
        </div>
      </div>
    </div>
    <div v-if="needToShowMedia" class="post-gallery mt-5">
      <div
        v-if="onePost.image_url && onePost.image_url.length === 2"
        v-show="imageToShow === 0"
        class="flex justify-between"
      >
        <div class="w-1/2 mr-2 overflow-hidden text-center flex justify-center">
          <img
            :src="onePost.image_url[0]"
            class=""
            loading="lazy"
            style="max-width:760px;height:507px;"
          />
        </div>
        <div class="w-1/2 overflow-hidden text-center flex justify-center">
          <img
            :src="onePost.image_url[1]"
            class=""
            loading="lazy"
            style="max-width:760px;height:507px;"
          />
        </div>
      </div>
      <div
        v-if="onePost.image_url && onePost.image_url.length === 3"
        v-show="imageToShow === 0"
        class="flex justify-between"
      >
        <div class="w-1/2 mr-2 overflow-hidden text-center flex justify-center">
          <img
            :src="onePost.image_url[0]"
            class=""
            loading="lazy"
            style="max-width:760px;height:507px;"
          />
        </div>
        <div
          class="w-1/2 overflow-hidden text-center flex flex-col"
          style="height: 507px;"
        >
          <div
            class="w-full mb-2 overflow-hidden text-center flex justify-center"
          >
            <img
              :src="onePost.image_url[1]"
              class=""
              loading="lazy"
              style="max-width:760px;height:507px;"
            />
          </div>
          <div class="w-full overflow-hidden text-center flex justify-center">
            <img
              :src="onePost.image_url[2]"
              class=""
              loading="lazy"
              style="max-width:760px;height:507px;"
            />
          </div>
        </div>
      </div>
      <div
        v-if="onePost.image_url && onePost.image_url.length >= 4"
        v-show="imageToShow === 0"
        class="flex justify-between"
      >
        <div
          class="w-1/2 mr-2 overflow-hidden text-center flex flex-col"
          style="height: 507px;"
        >
          <div
            class="w-full mb-2 overflow-hidden text-center flex justify-center"
          >
            <img
              :src="onePost.image_url[0]"
              class=""
              loading="lazy"
              style="max-width:760px;height:507px;"
            />
          </div>
          <div class="w-full overflow-hidden text-center flex justify-center">
            <img
              :src="onePost.image_url[1]"
              class=""
              loading="lazy"
              style="max-width:760px;height:507px;"
            />
          </div>
        </div>
        <div
          class="w-1/2 overflow-hidden text-center flex flex-col"
          style="height: 507px;"
        >
          <div
            class="w-full mb-2 overflow-hidden text-center flex justify-center"
          >
            <img
              :src="onePost.image_url[2]"
              class=""
              loading="lazy"
              style="max-width:760px;height:507px;"
            />
          </div>
          <div class="w-full overflow-hidden text-center flex justify-center">
            <img
              :src="onePost.image_url[3]"
              class=""
              loading="lazy"
              style="max-width:760px;height:507px;"
            />
          </div>
        </div>
      </div>
      <div
        v-for="(image, index) in onePost.image_url"
        v-show="imageToShow === index + 1"
        :key="`image-${index}`"
        class="overflow-hidden text-center flex justify-center"
      >
        <img
          :src="image"
          class=""
          loading="lazy"
          style="max-width:760px;height:507px;"
        />
      </div>
      <div
        v-for="(video, index) in onePost.video_url"
        v-show="imageToShow === index + 1 + onePost.image_url.length"
        :key="`video-${index}`"
        class="overflow-hidden text-center flex justify-center"
      >
        <iframe
          :src="getIframeSrc(video)"
          width="760"
          height="507"
          frameborder="0"
        ></iframe>
      </div>
      <div v-if="needToShowMedia > 1" class="mt-5 flex justify-center">
        <div
          class="rounded-full border-2 border-blue-default w-4 h-4 mx-1 cursor-pointer"
          :class="{ 'bg-blue': imageToShow === 0 }"
          @click="imageToShow = 0"
        ></div>
        <div
          v-for="n in needToShowMedia"
          :key="n"
          :class="{ 'bg-blue': imageToShow === n }"
          class="rounded-full border-2 border-blue-default w-4 h-4 mx-1 cursor-pointer"
          @click="imageToShow = n"
        ></div>
      </div>
    </div>
    <div class="post-causes-tags">
      <span
        v-if="
          onePost.kynd_code &&
            onePost.kynd_code !== '' &&
            onePost.kynd_code !== ' '
        "
        class=""
        >Kynd Code: {{ onePost.kynd_code }}</span
      >
      <span v-if="onePost.tags.length" class=""
        >Tags:
        <span v-for="(tag, index) in onePost.tags" :key="tag.id">
          <router-link :to="{ name: 'tag', params: { tagId: tag.id } }"
            >#{{ tag.name }}</router-link
          ><span v-if="index + 1 < onePost.tags.length">, </span>
        </span></span
      >
      <span v-if="onePost.causes.length" class=""
        >Causes:
        <span v-for="(cause, index) in onePost.causes" :key="cause.id">
          <router-link :to="{ name: 'cause', params: { causeId: cause.id } }">{{
            cause.name
          }}</router-link
          ><span v-if="index + 1 < onePost.causes.length">, </span>
        </span></span
      >
      <span v-if="onePost.friends.length" class=""
        >Friends:
        <span v-for="(friend, index) in onePost.friends" :key="friend.id">
          <router-link
            :to="{ name: 'user-page', params: { userId: friend.id } }"
            >{{ friend.name }}</router-link
          ><span v-if="index + 1 < onePost.friends.length">, </span>
        </span></span
      >
      <span v-if="onePost.challenged.length" class=""
        >Challenged:
        <span
          v-for="(challenge, index) in onePost.challenged"
          :key="challenge.id"
        >
          <router-link
            :to="{ name: 'user-page', params: { userId: challenge.id } }"
            >{{ challenge.name }}</router-link
          ><span v-if="index + 1 < onePost.challenged.length">, </span>
        </span></span
      >
    </div>
    <div class="post-meta" :class="[isLast ? 'border-b-0' : '']">
      <a
        :class="[onePost.is_likeable ? 'cursor-pointer' : 'cursor-default']"
        @click="addLike"
      >
        <BaseIcon
          class="inline-block -mb-1 mr-1"
          :name="onePost.is_likeable ? 'like' : 'liked'"
          :size="24"
        />
        <span class="mr-2 align-middle">{{
          onePost.post_likes.length ? onePost.post_likes.length : ''
        }}</span></a
      >
      <a class="cursor-pointer" @click="showComment = !showComment"
        ><svg
          xmlns="http://www.w3.org/2000/svg"
          width="22"
          height="20"
          viewBox="0 0 22 20"
          class="inline-block mr-2"
        >
          <g
            id="Group_42"
            data-name="Group 42"
            transform="translate(-2473.777 -866)"
          >
            <path
              id="Path_39"
              data-name="Path 39"
              d="M2473.777,885.611l1.8-6.317a8.371,8.371,0,0,1-.156-7.619,9.331,9.331,0,0,1,2.278-2.965,11.579,11.579,0,0,1,14.886,0,9.332,9.332,0,0,1,2.278,2.965,8.367,8.367,0,0,1,0,7.311,9.332,9.332,0,0,1-2.278,2.965,11.243,11.243,0,0,1-7.443,2.709,11.5,11.5,0,0,1-5.9-1.588Zm11.36-18.553c-5.24,0-9.5,3.711-9.5,8.272a7.327,7.327,0,0,0,.968,3.64l.109.192-1.283,4.516,3.886-1.807.244.154a10.4,10.4,0,0,0,5.58,1.577c5.24,0,9.5-3.711,9.5-8.272S2490.377,867.058,2485.137,867.058Z"
              fill="#069bd7"
            />
          </g>
        </svg>
        <span class="mr-2 align-middle">{{
          onePost.comments.length ? onePost.comments.length : ''
        }}</span></a
      >
      <div v-show="showComment" class="comments-container">
        <div class="bg-blue-lighter rounded-comments p-1">
          <CommentItem
            v-for="comment in onePost.comments"
            :key="comment.id"
            :one-comment="comment"
            @like-comment="likeComment"
          >
          </CommentItem>
          <div class="comment-form">
            <textarea
              v-model="newComment"
              placeholder="Write A Reply"
              class="bg-gray-lighter"
            >
            </textarea>
            <button
              class="button primary-button leading-relax text-sml rounded-small-button px-3"
              @click="postComment"
            >
              Comment
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- <div class="text-center -mt-4">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000.41 1000" fill="#924CD8" height="28" width="44" class="mx-auto bg-white px-2"><title>Kyndhub_LinkIcon</title><path d="M926.08,73.68c-98.51-98.24-257.94-98.24-356.44,0L409,237.81a396.38,396.38,0,0,1,69,12.71l132.54-136A194.34,194.34,0,0,1,885.41,389.31L710.09,564.66a192.87,192.87,0,0,1-137.62,56.86c-52,0-146.44-72.91-183.16-109.62A194,194,0,0,1,360,474.49a49.8,49.8,0,0,0-13.94,9.85l-29.42,29.44a821.84,821.84,0,0,0,81.12,84.72c128.15,97.64,254.62,105.3,353,7L926.07,430.12c98.6-98.52,98.95-258,.43-356.6C926.46,73.47,926.41,73.43,926.08,73.68Z" transform="translate(0.2)"></path><path d="M389.21,885.25A194.34,194.34,0,1,1,114.4,610.37L289.75,435a194.49,194.49,0,0,1,274.84,0c11.4,11.41,73.93,73.17,82.1,86.75a49.63,49.63,0,0,0,14-9.91l29.38-29.41a381.14,381.14,0,0,0-32-39c-77.92-88.78-163.4-123-230.91-123A250.55,250.55,0,0,0,249,394.25L73.66,569.67A252,252,0,1,0,430,926.18L601.41,758.31a393.87,393.87,0,0,1-68.59-13.07Z" transform="translate(0.2)"></path></svg>
    </div> -->
    <PostEdit
      v-if="showPostEdit"
      :one-post="onePost"
      @save-status-update="saveStatusUpdate"
      @close="closePostEdit"
    ></PostEdit>
    <UserProfile
      v-if="userProfile"
      :user="userProfile"
      @close="closeUserView"
    ></UserProfile>
  </div>
</template>
